import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import VideocamIcon from '@mui/icons-material/Videocam';
import { useNavigate } from 'react-router-dom';
import { getClientList } from '../../services/clientService';

const ClientDirectoryPage = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [error, setError] = useState('');

  const deriveName = (client) => {
    if (client.name) return client.name;
    const fullName = [client.first_name, client.last_name].filter(Boolean).join(' ');
    if (fullName) return fullName;
    if (client.user_detail?.full_name) return client.user_detail.full_name;
    if (client.user?.username) return client.user.username;
    return `Client ${client.id}`;
  };

  useEffect(() => {
    const loadClients = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await getClientList();
        const enhanced = data.map((client) => ({
          ...client,
          displayName: deriveName(client),
          fall_events_total: client.fall_events_total ?? client.fall_count ?? 0,
          active_cameras: client.cameras?.length ?? client.camera_count ?? 0,
          last_event_at: client.last_event_at ?? client.last_fall_at ?? null,
        }));
        setClients(enhanced);
        setFiltered(enhanced);
      } catch (err) {
        setError('Unable to load clients at the moment. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadClients();
  }, []);

  useEffect(() => {
    if (!query) {
      setFiltered(clients);
      return;
    }
    const lower = query.toLowerCase();
    setFiltered(
      clients.filter((client) => {
        const nameMatch = client.displayName?.toLowerCase()?.includes(lower);
        const locationAddress = client.address || client.location || client.user_detail?.address || '';
        const locationMatch = locationAddress.toLowerCase().includes(lower);
        const idMatch = client.id?.toString()?.includes(lower);
        return nameMatch || locationMatch || idMatch;
      }),
    );
  }, [query, clients]);

  const summary = useMemo(() => {
    if (!clients.length) {
      return { total: 0, fallsToday: 0, watchers: 0 };
    }
    const totalFalls = clients.reduce((acc, client) => acc + (client.fall_events_total || 0), 0);
    const fallsToday = clients.reduce(
      (acc, client) => acc + (client.falls_today ?? client.todays_fall_count ?? 0),
      0,
    );
    return { total: clients.length, fallsToday, totalFalls };
  }, [clients]);

  return (
    <Container maxWidth="xl" sx={{ py: 5 }}>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
        >
          <Stack spacing={1}>
            <Typography variant="h4" fontWeight={700}>
              Clients
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Review residents at a glance. Jump into a client workspace to see their incidents, cameras, and clips.
            </Typography>
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ width: { xs: '100%', md: 'auto' } }}>
            <TextField
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              variant="outlined"
              placeholder="Search clients by name, location or ID"
              size="small"
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              sx={{ width: { xs: '100%', md: 320 } }}
            />
            <Button
              variant="contained"
              onClick={() => setQuery('')}
              disabled={!query}
            >
              Clear
            </Button>
          </Stack>
        </Stack>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <SummaryTile
            icon={<PeopleAltIcon color="primary" />}
            label="Total clients"
            value={summary.total}
          />
          <SummaryTile
            icon={<WarningAmberIcon color={summary.fallsToday > 0 ? 'warning' : 'action'} />}
            label="Falls today"
            value={summary.fallsToday}
          />
          <SummaryTile
            icon={<VideocamIcon color="action" />}
            label="All-time falls"
            value={summary.totalFalls}
          />
        </Stack>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : filtered.length === 0 ? (
          <Card sx={{ p: 4, borderRadius: '12px !important' }}>
            <Typography variant="h6" gutterBottom>
              No clients found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try adjusting your search or filters.
            </Typography>
          </Card>
        ) : (
          <Grid container spacing={3}>
            {filtered.map((client) => (
              <Grid item key={client.id} xs={12} sm={6} md={4} xl={3}>
                <ClientCard client={client} onClick={() => navigate(`/clients/${client.id}`)} />
              </Grid>
            ))}
          </Grid>
        )}
      </Stack>
    </Container>
  );
};

const SummaryTile = ({ icon, label, value }) => (
  <Card
    sx={{
      flex: 1,
      p: 3,
      borderRadius: '12px !important',
      display: 'flex',
      alignItems: 'center',
      gap: 2,
    }}
  >
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: 2,
        bgcolor: 'rgba(122, 59, 193, 0.08)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {icon}
    </Box>
    <Stack spacing={0.5}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5" fontWeight={700}>
        {value}
      </Typography>
    </Stack>
  </Card>
);

const ClientCard = ({ client, onClick }) => {
  const address = client.address || client.location || client.user_detail?.address || 'Location not specified';
  const displayName = client.displayName || 'Unnamed client';

  return (
  <Card
    sx={{ borderRadius: '12px !important', height: '100%' }}
  >
    <CardActionArea
      onClick={onClick}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        height: '100%',
        p: 3,
        gap: 2,
      }}
    >
      <Stack spacing={0.5}>
        <Typography variant="h6" fontWeight={600}>
          {displayName}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {address}
        </Typography>
      </Stack>

      <Stack direction="row" spacing={1} flexWrap="wrap">
        <Chip
          label={`${client.active_cameras ?? 0} cameras`}
          size="small"
          sx={{ borderRadius: 1 }}
        />
        <Chip
          label={`${client.fall_events_total ?? 0} recorded falls`}
          size="small"
          color={client.fall_events_total > 0 ? 'warning' : 'default'}
          sx={{ borderRadius: 1 }}
        />
        {client.status && (
          <Chip
            label={client.status.replace(/_/g, ' ').toLowerCase()}
            size="small"
            color={client.status === 'ACTIVE' ? 'success' : 'default'}
            sx={{ borderRadius: 1, textTransform: 'capitalize' }}
          />
        )}
      </Stack>

      <Stack spacing={0.5} sx={{ flexGrow: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Last fall:
        </Typography>
        <Typography variant="body2" fontWeight={500}>
          {client.last_event_at
            ? new Date(client.last_event_at).toLocaleString()
            : 'No incidents recorded'}
        </Typography>
      </Stack>

      <Button variant="text" size="small" sx={{ alignSelf: 'flex-start' }}>
        Open workspace
      </Button>
    </CardActionArea>
  </Card>
  );
};

export default ClientDirectoryPage;
